import React, { useMemo } from "react";
import moment from "moment";
import { CALENDAR_ACCENT, colorWithOpacity } from "./CalendarConstants";
import { isAllDay, isMultiDay, spanDays, dateKey, getEventColor } from "./CalendarUtils";
import { HOLIDAYS, school_holidays } from "./CalendarUtils";

const MAX_LANES = 3;
const LANE_HEIGHT = 16;
const LANE_SPACING = 1;
const DAY_NUMBER_HEIGHT = 22;

const DAY_NAMES = ["MO", "DI", "MI", "DO", "FR", "SA", "SO"];

const MonthView = ({ focusedDay, events, onDaySelected, onEventTap }) => {
  const mFocused = useMemo(() => moment(focusedDay), [focusedDay]);
  const today = useMemo(() => moment(), []);

  // Grid calculations
  const gridStart = useMemo(() => {
    const first = mFocused.clone().startOf("month");
    const weekday = first.isoWeekday(); // 1=Mon
    return first.clone().subtract(weekday - 1, "days");
  }, [mFocused]);

  const weekCount = useMemo(() => {
    const first = mFocused.clone().startOf("month");
    const last = mFocused.clone().endOf("month");
    const startOffset = first.isoWeekday() - 1;
    return Math.ceil((last.date() + startOffset) / 7);
  }, [mFocused]);

  // Collect all events in the visible grid range
  const allEvents = useMemo(() => {
    const gridEnd = gridStart.clone().add(weekCount * 7 - 1, "days");
    const result = [];
    const seen = new Set();

    // Walk each day in range
    let d = gridStart.clone();
    while (d.isSameOrBefore(gridEnd, "day")) {
      const key = d.format("YYYY-MM-DD");
      const dayEvents = events.filter((e) => dateKey(e.start) === key);
      for (const e of dayEvents) {
        const id = e.id || `${e.title}-${moment(e.start).valueOf()}`;
        if (!seen.has(id)) {
          seen.add(id);
          result.push(e);
        }
      }
      d.add(1, "day");
    }

    // Find multi-day events starting before the range that extend into it
    const searchStart = gridStart.clone().subtract(31, "days");
    d = searchStart.clone();
    while (d.isBefore(gridStart, "day")) {
      const key = d.format("YYYY-MM-DD");
      const dayEvents = events.filter((e) => dateKey(e.start) === key);
      for (const e of dayEvents) {
        if (isMultiDay(e) && moment(e.end).isSameOrAfter(gridStart, "day")) {
          const id = e.id || `${e.title}-${moment(e.start).valueOf()}`;
          if (!seen.has(id)) {
            seen.add(id);
            result.push(e);
          }
        }
      }
      d.add(1, "day");
    }

    return result;
  }, [events, gridStart, weekCount]);

  // Build week rows
  const weeks = useMemo(() => {
    return Array.from({ length: weekCount }, (_, weekIdx) => {
      const weekDays = Array.from({ length: 7 }, (_, d) =>
        gridStart.clone().add(weekIdx * 7 + d, "days")
      );
      const lanes = allocateLanes(weekDays, allEvents);
      const overflow = overflowCounts(lanes, 7);
      return { weekDays, lanes, overflow };
    });
  }, [weekCount, gridStart, allEvents]);

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      {/* Weekday headers */}
      <div style={{
        display: "flex",
        backgroundColor: "#fff",
        borderBottom: "1px solid #E0E0E0",
        padding: "6px 0",
      }}>
        {DAY_NAMES.map((name) => (
          <div key={name} style={{
            flex: 1,
            textAlign: "center",
            fontSize: 11,
            fontWeight: 600,
            color: "#9E9E9E",
          }}>
            {name}
          </div>
        ))}
      </div>

      {/* Week rows */}
      <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
        {weeks.map(({ weekDays, lanes, overflow }, weekIdx) => (
          <WeekRow
            key={weekIdx}
            weekDays={weekDays}
            lanes={lanes}
            overflow={overflow}
            focusedMonth={mFocused.month()}
            today={today}
            onDaySelected={onDaySelected}
            onEventTap={onEventTap}
          />
        ))}
      </div>
    </div>
  );
};

/** Single week row in the month grid */
const WeekRow = React.memo(({
  weekDays, lanes, overflow, focusedMonth, today, onDaySelected, onEventTap,
}) => {
  const weekStart = weekDays[0];
  const weekEnd = weekDays[6];

  return (
    <div style={{ flex: 1, position: "relative", minHeight: 0, overflow: "hidden" }}>
      {/* Day cells */}
      <div style={{ display: "flex", height: "100%" }}>
        {weekDays.map((day, col) => {
          const isCurrentMonth = day.month() === focusedMonth;
          const isToday = day.isSame(today, "day");
          const dayStr = day.format("YYYY-MM-DD");
          const isPublicHoliday = HOLIDAYS.some((h) => h.date === dayStr);
          const isSchoolHoliday = school_holidays.some((h) =>
            day.isBetween(moment(h.start), moment(h.end), "day", "[]")
          );

          let bgColor = "#fff";
          if (isPublicHoliday) bgColor = "#fff1f0";
          else if (isSchoolHoliday) bgColor = "#f0f7ff";

          return (
            <div
              key={dayStr}
              onClick={() => onDaySelected(day.toDate())}
              style={{
                flex: 1,
                borderBottom: "1px solid #F5F5F5",
                borderRight: "1px solid #F5F5F5",
                backgroundColor: bgColor,
                cursor: "pointer",
                paddingTop: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div style={{
                width: 24,
                height: 24,
                lineHeight: "24px",
                textAlign: "center",
                borderRadius: "50%",
                fontSize: 12,
                fontWeight: isToday ? "bold" : 500,
                color: isToday ? "#fff" : isCurrentMonth ? "rgba(0,0,0,0.87)" : "#BDBDBD",
                backgroundColor: isToday ? CALENDAR_ACCENT : "transparent",
              }}>
                {day.date()}
              </div>
              {/* Overflow indicator */}
              {overflow[col] > 0 && (
                <div style={{
                  marginTop: "auto",
                  paddingBottom: 1,
                  fontSize: 9,
                  fontWeight: 600,
                  color: "#9E9E9E",
                }}>
                  +{overflow[col]}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Event lane bars (overlaid) */}
      {lanes.slice(0, MAX_LANES).map((lane, laneIdx) => {
        const laneTop = DAY_NUMBER_HEIGHT + laneIdx * (LANE_HEIGHT + LANE_SPACING);

        return (
          <React.Fragment key={laneIdx}>
            {lane.map((entry, i) => {
              const color = getEventColor(entry.event);
              const leftPct = (entry.startCol / 7) * 100;
              const widthPct = ((entry.endCol - entry.startCol + 1) / 7) * 100;

              const eStart = moment(entry.event.start).startOf("day");
              const eEnd = moment(entry.event.end).startOf("day");
              const roundLeft = eStart.isSameOrAfter(weekStart, "day");
              const roundRight = eEnd.isSameOrBefore(weekEnd, "day");

              return (
                <div
                  key={`${laneIdx}-${i}`}
                  onClick={(e) => { e.stopPropagation(); onEventTap(entry.event); }}
                  style={{
                    position: "absolute",
                    top: laneTop,
                    left: `calc(${leftPct}% + 1px)`,
                    width: `calc(${widthPct}% - 2px)`,
                    height: LANE_HEIGHT,
                    backgroundColor: colorWithOpacity(color, 0.2),
                    borderRadius: `${roundLeft ? 3 : 0}px ${roundRight ? 3 : 0}px ${roundRight ? 3 : 0}px ${roundLeft ? 3 : 0}px`,
                    borderLeft: roundLeft ? `2.5px solid ${color}` : "none",
                    padding: "0 4px",
                    display: "flex",
                    alignItems: "center",
                    overflow: "hidden",
                    cursor: "pointer",
                    boxSizing: "border-box",
                  }}
                >
                  <span style={{
                    fontSize: 10,
                    fontWeight: 500,
                    color: colorWithOpacity(color, 0.9),
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    {entry.event.title}
                  </span>
                </div>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
});

// ── Lane allocation algorithm ────────────────────────────────────

function allocateLanes(weekDays, allEvents) {
  const weekStart = weekDays[0];
  const weekEnd = weekDays[6];

  // Filter events overlapping this week
  const relevant = allEvents.filter((e) => {
    const eStart = moment(e.start).startOf("day");
    const eEnd = moment(e.end).startOf("day");
    return eEnd.isSameOrAfter(weekStart, "day") && eStart.isSameOrBefore(weekEnd, "day");
  });

  // Sort: multi-day/all-day first (longer span first), then by start
  relevant.sort((a, b) => {
    const aMulti = isMultiDay(a) || isAllDay(a);
    const bMulti = isMultiDay(b) || isAllDay(b);
    if (aMulti && !bMulti) return -1;
    if (!aMulti && bMulti) return 1;
    if (aMulti && bMulti) {
      const cmp = spanDays(b) - spanDays(a);
      if (cmp !== 0) return cmp;
    }
    return moment(a.start).diff(moment(b.start));
  });

  const lanes = []; // lanes[i] = array of { event, startCol, endCol }

  for (const event of relevant) {
    const eStart = moment(event.start).startOf("day");
    const eEnd = moment(event.end).startOf("day");

    const visStart = eStart.isBefore(weekStart, "day") ? weekStart : eStart;
    const visEnd = eEnd.isAfter(weekEnd, "day") ? weekEnd : eEnd;

    const startCol = Math.max(0, Math.min(6, visStart.diff(weekStart, "days")));
    const endCol = Math.max(0, Math.min(6, visEnd.diff(weekStart, "days")));

    // Find first lane where columns are free
    let placed = -1;
    for (let i = 0; i < lanes.length; i++) {
      let free = true;
      for (const entry of lanes[i]) {
        if (!(endCol < entry.startCol || startCol > entry.endCol)) {
          free = false;
          break;
        }
      }
      if (free) {
        lanes[i].push({ event, startCol, endCol });
        placed = i;
        break;
      }
    }
    if (placed === -1) {
      lanes.push([{ event, startCol, endCol }]);
    }
  }

  return lanes;
}

function overflowCounts(lanes, columnCount) {
  const counts = new Array(columnCount).fill(0);
  for (let laneIdx = MAX_LANES; laneIdx < lanes.length; laneIdx++) {
    for (const entry of lanes[laneIdx]) {
      for (let c = entry.startCol; c <= entry.endCol; c++) {
        counts[c]++;
      }
    }
  }
  return counts;
}

export default MonthView;
